import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { FaCheck } from 'react-icons/fa';
import Toggle from '../components/Toggle';
import InfoPopup from '../components/InfoPopup';
import { trunkOptions } from '../components/EpikBoxes/constants';
import './CustomTemplateEditModal.css';

const FIELD_LABELS = {
  CPCDelayTime: 'CPC Delay Time (in milliseconds)',
  CPCDuration: 'CPC Duration (in milliseconds)',
  OnHookTipRingVoltage: 'Onhook Volts',
  OffHookCurrentMax: 'Offhook Current',
  HVICPowerThreshold: 'HVIC Power Threshold',
  DTMFDetectMinLength: 'DTMF Detect Length',
  DTMFDetectMinGap: 'DTMF Detect Gap',
  ChannelTxGain: 'TX Gain',
  ChannelRxGain: 'RX Gain',
  DTMFMethod: 'DTMF Method',
  DTMFPlaybackLevel: 'DTMF Playback Level',
  RingVoltage: 'Ring Voltage',
  DigitMapShortTimer: 'Digit Map Short Timer',
  SilenceDetectSensitivity: 'Silence Detect',
};

const FIELD_ORDER = [
  'CPCDelayTime',
  'CPCDuration',
  'OnHookTipRingVoltage',
  'OffHookCurrentMax',
  'HVICPowerThreshold',
  'DTMFDetectMinLength',
  'DTMFDetectMinGap',
  'ChannelTxGain',
  'ChannelRxGain',
  'DTMFMethod',
  'DTMFPlaybackLevel',
  'RingVoltage',
  'DigitMapShortTimer',
  'SilenceDetectSensitivity',
];

const DROPDOWN_FIELDS = {
  DTMFMethod: ['auto', 'inband', 'rfc2833'],
  HVICPowerThreshold: ['2.5W', '2.0W', '1.5W', '1.0W'],
};

const hasValue = value => value !== undefined && value !== null && value !== '';

const ToggleRow = ({
  title,
  infoId,
  value,
  onToggleChange,
  left = 'Disable',
  right = 'Enable',
}) => (
  <Row className="mb-4 align-items-center">
    <Col xs="5" className="d-flex align-items-start custom-template-label-col">
      <Label className="mb-0 custom-template-label">{title}</Label>
      <sup className="ml-1">
        <InfoPopup id={infoId} text={title} />
      </sup>
    </Col>

    <Col xs="7">
      <Toggle
        left={left}
        right={right}
        value={!!value}
        onToggle={() => onToggleChange(!value)}
      />
    </Col>
  </Row>
);

const CustomTemplateEditModal = ({
  isOpen,
  isClose,
  templateData,
  updateCustomTemplate,
}) => {
  const [formData, setFormData] = useState({});
  const [showEpiConfig, setShowEpiConfig] = useState(false);
  console.log('mian formData', formData);


  useEffect(() => {
    if (templateData) {
      setFormData(templateData);
      setShowEpiConfig(false);
    }
  }, [templateData]);

  const visibleFields = useMemo(() => {
    if (!templateData) return [];

    return FIELD_ORDER.filter(field =>
      Object.prototype.hasOwnProperty.call(templateData, field)
    );
  }, [templateData]);

  const hasEpiConfig = useMemo(() => {
    if (!templateData) return false;

    const lineType = (templateData?.name || '').toLowerCase();

    return (
      (templateData?.ataType === 'granite' || lineType.includes('granite')) &&
      (
        hasValue(templateData.vadEnable) ||
        hasValue(templateData.JitterBufferType) ||
        hasValue(templateData.JitterMinDelay) ||
        hasValue(templateData.JitterMaxDelay)
      )
    );
  }, [templateData]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!templateData?._id) return;

    const payload = {
      ...templateData,
      ...formData,
      companyBox: formData.companyBox?._id || formData.companyBox,
    };

    const response = await updateCustomTemplate(templateData._id, payload);

    if (response) {
      isClose(true);
    }
  };

  if (!templateData) return null;

  return (
    <Modal isOpen={isOpen} className="custom-template-edit-modal" centered>
      <ModalHeader className="custom-template-modal-header">
        <div className="custom-template-modal-header-inner">
          <h5 className="custom-template-modal-title mb-0">
            {showEpiConfig ? 'EPI Configuration' : 'Port Configuration'}
          </h5>

          {hasEpiConfig && (
            <Button
              color={showEpiConfig ? 'primary' : 'outline-primary'}
              size="sm"
              className="custom-template-switch-btn"
              onClick={() => setShowEpiConfig(!showEpiConfig)}
            >
              {showEpiConfig ? 'Port Configuration' : 'EPI Configuration'}
            </Button>
          )}
        </div>
      </ModalHeader>

      <ModalBody className="custom-template-edit-body">
        {showEpiConfig ? (
          <>
            <Row className="mb-2 custom-template-header-row">
              <Col xs="5" />
              <Col xs="7">
                <h6 className="custom-template-column-title">Database Settings</h6>
              </Col>
            </Row>

            {hasValue(formData.vadEnable) && (
              <ToggleRow
                title="VAD"
                infoId="customTemplateVadEnable"
                value={formData.vadEnable}
                onToggleChange={val => handleChange('vadEnable', val)}
              />
            )}

            {hasValue(formData.JitterBufferType) && (
              <ToggleRow
                title="Jitter Buffer Type"
                infoId="customTemplateJitterBufferType"
                left="Fixed"
                right="Adaptive"
                value={formData.JitterBufferType === 'Adaptive'}
                onToggleChange={val =>
                  handleChange('JitterBufferType', val ? 'Adaptive' : 'Fixed')
                }
              />
            )}

            {hasValue(formData.JitterMinDelay) && (
              <Row className="mb-4 align-items-center">
                <Col xs="5" className="d-flex align-items-start custom-template-label-col">
                  <Label className="mb-0 custom-template-label">
                    Jitter Min Value
                  </Label>
                  <sup className="ml-1">
                    <InfoPopup
                      id="customTemplateJitterMinDelay"
                      text="Jitter Min Value"
                    />
                  </sup>
                </Col>

                <Col xs="7">
                  <div className="template-input-wrap">
                    <Input
                      type="text"
                      value={formData.JitterMinDelay ?? ''}
                      onChange={e => handleChange('JitterMinDelay', e.target.value)}
                      className="template-input"
                    />
                    <FaCheck className="template-green-check" />
                  </div>
                </Col>
              </Row>
            )}

            {hasValue(formData.JitterMaxDelay) && (
              <Row className="mb-4 align-items-center">
                <Col xs="5" className="d-flex align-items-start custom-template-label-col">
                  <Label className="mb-0 custom-template-label">
                    Jitter Max Value
                  </Label>
                  <sup className="ml-1">
                    <InfoPopup
                      id="customTemplateJitterMaxDelay"
                      text="Jitter Max Value"
                    />
                  </sup>
                </Col>

                <Col xs="7">
                  <div className="template-input-wrap">
                    <Input
                      type="text"
                      value={formData.JitterMaxDelay ?? ''}
                      onChange={e => handleChange('JitterMaxDelay', e.target.value)}
                      className="template-input"
                    />
                    <FaCheck className="template-green-check" />
                  </div>
                </Col>
              </Row>
            )}
          </>
        ) : (
          <>
            <Row className="mb-2 custom-template-header-row">
              <Col xs="5" />
              <Col xs="7">
                <h6 className="custom-template-column-title">
                  Database Settings
                </h6>
              </Col>
            </Row>

            {visibleFields.map(field => {
              if (field === 'SilenceDetectSensitivity') {
                return (
                  <ToggleRow
                    key={field}
                    title={FIELD_LABELS[field]}
                    infoId="customTemplateSilenceDetectSensitivity"
                    left="Med"
                    right="Low"
                    value={formData[field] === 'Low'}
                    onToggleChange={val =>
                      handleChange(field, val ? 'Low' : 'Medium')
                    }
                  />
                );
              }

              return (
                <Row key={field} className="mb-4 align-items-center">
                  <Col xs="5" className="d-flex align-items-start custom-template-label-col">
                    <Label className="mb-0 custom-template-label">
                      {FIELD_LABELS[field] || field}
                    </Label>
                    <sup className="ml-1">
                      <InfoPopup
                        id={`customTemplate${field}`}
                        text={FIELD_LABELS[field] || field}
                      />
                    </sup>
                  </Col>

                  <Col xs="7">
                    <div className="template-input-wrap">
                      {DROPDOWN_FIELDS[field] ? (
                        <Input
                          type="select"
                          value={formData[field] || ''}
                          onChange={e => handleChange(field, e.target.value)}
                          className="template-input"
                        >
                          {DROPDOWN_FIELDS[field].map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Input>
                      ) : (
                        <>
                          <Input
                            type="text"
                            value={formData[field] ?? ''}
                            onChange={e => handleChange(field, e.target.value)}
                            className="template-input"
                          />
                          <FaCheck className="template-green-check" />
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              );
            })}

            {templateData?.showInbandRouteToggle && (
              <ToggleRow
                title="Inband Route"
                infoId="customTemplateInbandRoute"
                value={formData.inbandRoute}
                onToggleChange={val => handleChange('inbandRoute', val)}
              />
            )}

              <Row className="mb-4 align-items-center">
                <Col xs="5" className="d-flex align-items-start custom-template-label-col">
                  <Label className="mb-0 custom-template-label">Trunk</Label>
                  <sup className="ml-1">
                    <InfoPopup
                      id="customTemplateTrunkSelection"
                      text="Trunk"
                    />
                  </sup>
                </Col>

                <Col xs="7">
                  <div className="template-input-wrap">
                    <Input
                      type="select"
                      value={formData.trunkSelection || ''}
                      onChange={e =>
                        handleChange('trunkSelection', e.target.value)
                      }
                      className="template-input"
                    >
                      {trunkOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              </Row>

            {(templateData?.showModemModeToggle ||
              templateData?.ataType === 'granite') && (
                <ToggleRow
                  title="Modem Mode"
                  infoId="customTemplateModemMode"
                  value={formData.modemMode}
                  onToggleChange={val => handleChange('modemMode', val)}
                />
              )}

            {(templateData?.showT38EnableToggle ||
              templateData?.ataType === 'granite') && (
                <ToggleRow
                  title="T38 Enable"
                  infoId="customTemplateT38Enabled"
                  value={formData.t38Enabled}
                  onToggleChange={val => handleChange('t38Enabled', val)}
                />
              )}

            {(templateData?.showFaxEnabledToggle ||
              templateData?.ataType === 'granite') && (
                <ToggleRow
                  title="Fax Event"
                  infoId="customTemplateFaxEnabled"
                  value={formData.faxEnabled}
                  onToggleChange={val => handleChange('faxEnabled', val)}
                />
              )}

            {templateData?.showModemOnlyToggle && (
              <ToggleRow
                title="Modem Only"
                infoId="customTemplateModemOnly"
                value={formData.modemOnly}
                onToggleChange={val => handleChange('modemOnly', val)}
              />
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter className="custom-template-footer">
        <Button color="secondary" outline onClick={() => isClose(false)}>
          Cancel
        </Button>

        <Button color="primary" onClick={handleSave}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomTemplateEditModal;